import { useEffect, useMemo, useState } from "react";
import { Vocabulary } from "../Utils/Vocabulary";
import GenericModal from "./GenericComponents/GenericModal";
import { toast } from "react-toastify";
import { getData } from "../Services/getData";
import { urlEnum } from "../Utils/UrlEnum";
import { Box, Button, Checkbox, FormControlLabel, Stack } from "@mui/material";

type Category = {
  id: number;
  name: string;
};

type SyncWordPressDataProsp = {
  wpUrl: string;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

export default function SyncWordPressData({
  wpUrl,
  open,
  onClose,
  onSuccess,
}: SyncWordPressDataProsp) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);

  const syncWordPressData = async (wpUlr: string) => {
    try {
      const url = `${wpUlr}/${
        selectedCategories.length !== 0 && `${selectedCategories.join(",")}`
      }`;
      await getData(url);
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (e) {
      toast.error(`${e}`);
    }
  };

  async function getCategories() {
    try {
      const categories = (await getData(`${urlEnum.products}/wpCategories`))
        .data;
      setCategories(categories);
      console.log(categories);
    } catch (error) {
      toast.error(`${error}`);
    }
  }
  const handleCheckboxChange = (categoryId: number) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Build parent-child category map based on single-word parents
  const categoryMap = useMemo(() => {
    const map = new Map<Category, Category[]>();

    // Find single-word categories (parents)
    const singleWordCategories = categories.filter(
      (cat) => cat.name.trim().split(/\s+/).length === 1
    );

    // Find multi-word categories
    const multiWordCategories = categories.filter(
      (cat) => cat.name.trim().split(/\s+/).length > 1
    );

    // For each single-word category, find children that contain it as substring
    singleWordCategories.forEach((parent) => {
      const parentNameUpper = parent.name.toUpperCase();
      const children = multiWordCategories.filter((child) =>
        child.name.toUpperCase().includes(parentNameUpper)
      );
      map.set(parent, children);
    });

    // Find orphan multi-word categories (not matched to any parent)
    const matchedChildIds = new Set<number>();
    map.forEach((children) => {
      children.forEach((child) => matchedChildIds.add(child.id));
    });

    const orphanCategories = multiWordCategories.filter(
      (cat) => !matchedChildIds.has(cat.id)
    );

    // Add orphans as standalone parents with no children
    orphanCategories.forEach((orphan) => {
      map.set(orphan, []);
    });

    return map;
  }, [categories]);

  useEffect(() => {
    console.log(categories);
    getCategories();
  }, []);

  return (
    <GenericModal
      open={open}
      onClose={onClose}
      title={Vocabulary.selectCategories}
    >
      <Box sx={{ minWidth: 300 }}>
        <Stack spacing={0.5}>
          {Array.from(categoryMap.entries()).map(([parent, children]) => (
            <Box key={parent.id} sx={{ mb: children.length > 0 ? 2 : 0 }}>
              {/* Parent category */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedCategories.includes(parent.id)}
                    onChange={() => handleCheckboxChange(parent.id)}
                  />
                }
                label={parent.name}
              />
              {/* Child categories with indentation */}
              {children.map((child, index) => (
                <Box key={child.id} sx={{ mb: index === children.length - 1 ? 1 : 0 }}>
                  <FormControlLabel
                    sx={{ ml: 3 }}
                    control={
                      <Checkbox
                        checked={selectedCategories.includes(child.id)}
                        onChange={() => handleCheckboxChange(child.id)}
                      />
                    }
                    label={child.name}
                  />
                </Box>
              ))}
            </Box>
          ))}
        </Stack>

        <Stack
          direction="row"
          spacing={2}
          sx={{ mt: 3, justifyContent: "flex-end" }}
        >
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              syncWordPressData(wpUrl);
            }}
            disabled={selectedCategories.length === 0}
          >
            Sync
          </Button>
        </Stack>
      </Box>
    </GenericModal>
  );
}
